import React, { useMemo, useState } from 'react';
import ScheduleCell from './ScheduleCell';
import SportHomogeneityHeader from './SportHomogeneityHeader';
import BookingPopover from './BookingPopover';
import { TIME_SLOTS } from './constants';

/**
 * Build a lookup: occupancy[hourIndex][courtNumber] = booking.
 * A booking with duration N occupies N consecutive rows.
 * A booking with multiple courts occupies multiple columns at the same row.
 */
function buildOccupancyMatrix(assignedBookings) {
  const matrix = {};
  for (const b of assignedBookings) {
    const startStr = (b.time_slot || '').split('-')[0]; // "16:00"
    const startHour = parseInt(startStr?.split(':')?.[0], 10);
    if (Number.isNaN(startHour)) continue;
    const dur = b.duration || 1;
    for (let h = 0; h < dur; h++) {
      const hour = startHour + h;
      const idx = hour - 8; // TIME_SLOTS index
      if (idx < 0 || idx >= TIME_SLOTS.length) continue;
      if (!matrix[idx]) matrix[idx] = {};
      for (const c of b.courts || []) {
        const cn = c.court_number;
        if (!cn) continue;
        // Each cell carries the booking + the per-cell sport (in case a booking
        // covers two courts with different sports).
        matrix[idx][cn] = {
          ...b,
          primary_sport: c.sport || b.primary_sport,
        };
      }
    }
  }
  return matrix;
}

export default function ScheduleGrid({ schedule }) {
  const [selectedBooking, setSelectedBooking] = useState(null);

  const courts = useMemo(() => {
    if (!schedule) return [];
    const n = schedule.location?.courts_count || 5;
    return Array.from({ length: n }, (_, i) => i + 1);
  }, [schedule]);

  const occupancy = useMemo(
    () => buildOccupancyMatrix(schedule?.assigned_bookings || []),
    [schedule]
  );

  if (!schedule) return null;

  return (
    <>
      <div
        className="bg-white border-2 border-gray-400 rounded-2xl overflow-hidden"
        data-testid="schedule-grid-wrapper"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="schedule-grid">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="border-b-2 border-gray-400 px-2 py-1.5 text-left text-[10px] uppercase font-bold w-24 text-gray-800">
                  Time
                </th>
                {courts.map((c) => (
                  <th
                    key={c}
                    className="border-b-2 border-l-2 border-gray-400 px-1 py-1 text-center text-xs"
                  >
                    <SportHomogeneityHeader
                      courtNumber={c}
                      info={schedule.sport_homogeneity_per_court?.[String(c)]}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TIME_SLOTS.map((slot, idx) => (
                <tr key={slot.label} className="even:bg-gray-50">
                  <td className="border border-gray-300 px-2 py-1 font-mono text-[10px] whitespace-nowrap font-semibold text-gray-800">
                    {slot.label}
                  </td>
                  {courts.map((c) => {
                    const booking = occupancy[idx]?.[c];
                    return (
                      <ScheduleCell
                        key={`${slot.label}-${c}`}
                        booking={booking}
                        onClick={setSelectedBooking}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <BookingPopover
        booking={selectedBooking}
        open={!!selectedBooking}
        onClose={() => setSelectedBooking(null)}
      />
    </>
  );
}
